import { Card, Select, Tag, Typography, Divider, Space, Button, message } from "antd";

const { Text } = Typography;

// Helper function to check if a specific model is selected
// Handles both string values and array values (Ensemble)
const isModelSelected = (modalSelection, modelName) => {
  if (Array.isArray(modalSelection)) {
    return modalSelection.includes(modelName);
  }
  return modalSelection === modelName;
};

const AnalyticsConfigurationCard = ({
  modalSelection,
  dependentVariable,
  groupBy,
  onChangeModal,
  onChangeDependent,
  onChangeGroupBy,
  dependentOptions,
  groupByOptions,
  onConfigure,
  applying,
  applyResponse,
  MODEL_OPTIONS,
  ENSEMBLE_MODELS,
  onBackToDashboard,
}) => (
  <Card
    title={<span className="font-semibold text-3xl text-white font-mono">Analytics Configuration</span>}
    className="bg-[#141c31] shadow-sm pt-10"
  >
    {/* Model Selection */}
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <Text strong className="text-white font-serif text-lg">Model Selection</Text>
        <Text type="secondary" className="text-base !text-gray-100 font-serif">
          Select one model to analyze
        </Text>
      </div>

      <Select
        showSearch
        value={modalSelection ?? MODEL_OPTIONS[0].value}
        onChange={onChangeModal}
        className="w-full font-sans"
        size="large"
        placeholder="Select a model"
        options={MODEL_OPTIONS}
        allowClear
      />
    </div>

    <Divider className="my-5" />

    {/* Dependent Variable */}
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <Text strong className="text-white font-serif text-lg">Dependent Variable</Text>
        <Text type="secondary" className="text-base !text-gray-100 font-serif">
          Select one numeric column to analyze
        </Text>
      </div>

      <Select
        showSearch
        value={dependentVariable}
        onChange={onChangeDependent}
        className="w-full"
        size="large"
        placeholder="Select a column"
        options={dependentOptions}
        allowClear
      />
    </div>

    <Divider className="my-5" />

    {/* Group By Variables */}
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <Text strong className="text-white font-serif text-lg">Group By Variables</Text>
        <Text type="secondary" className="text-base !text-gray-100 font-serif">
          {isModelSelected(modalSelection, "benford") && !Array.isArray(modalSelection)
            ? "Not required for Benford Analysis" 
            : isModelSelected(modalSelection, "rsf") && !Array.isArray(modalSelection)
            ? "Required for RSF Analysis (Select up to 3 columns for grouping)" 
            : `Select up to 3 columns for grouping (${groupBy.length}/3)`}
        </Text>
      </div>

      <Select
        showSearch
        mode="multiple"
        value={groupBy}
        onChange={onChangeGroupBy}
        maxTagCount={3}
        size="large"
        allowClear
        className="w-full"
        placeholder={isModelSelected(modalSelection, "benford") && !Array.isArray(modalSelection) ? "Disabled for Benford analysis" : "Select columns to group by"}
        options={groupByOptions}
        disabled={isModelSelected(modalSelection, "benford") && !Array.isArray(modalSelection)}
        status={isModelSelected(modalSelection, "rsf") && !Array.isArray(modalSelection) && groupBy.length === 0 ? "error" : ""}
      />
      {/* <div className="mt-3 flex flex-wrap gap-2">
        {groupBy.map((g) => (
          <Tag
            key={g}
            closable
            onClose={(e) => {
              e.preventDefault();
              onChangeGroupBy(groupBy.filter((x) => x !== g));
            }}
            color="blue"
            className="rounded-full px-3 py-1"
          >
            {g}
          </Tag>
        ))}
      </div> */}
    </div>

    <Space direction="vertical" className="w-full">
      <Button
        type="primary"
        size="large"
        className="w-full font-serif"
        onClick={() => {
          // Validate RSF requirement (only for single RSF model, not Ensemble)
          if (isModelSelected(modalSelection, "rsf") && !Array.isArray(modalSelection) && groupBy.length === 0) {
            message.error("Group By Variables are required for RSF analysis");
            return;
          }
          
          // Handle Ensemble vs single model
          let modelsArray;
          if (modalSelection === "ensemble") {
            modelsArray = ENSEMBLE_MODELS;
          } else {
            modelsArray = [String(modalSelection).toLowerCase().replace(/\s|-/g, "")];
          }
          
          onConfigure({
            models: modelsArray,
            dep_variable: dependentVariable,
            groupby_columns: groupBy,
          });
        }}
        loading={applying}
        disabled={isModelSelected(modalSelection, "rsf") && !Array.isArray(modalSelection) && groupBy.length === 0}
      >
        Configure Analytics
      </Button>


      {applyResponse?.blob_download_link && (
        <Button
          type="default"
          size="large"
          className="w-full bg-green-600 hover:!bg-green-700 !text-white font-serif"
          onClick={() => onBackToDashboard()}
          disabled={applying}
        >
          View Analytics Results
        </Button>
      )}
    </Space>
  </Card>
);

export default AnalyticsConfigurationCard;